/**
 * SimpleAccessibilitySettings.js
 *
 * Hebrew/RTL accessibility settings panel. Visual language matches
 * frontend/src/pages/AccessibilityStatement.js (teal/mint gradient hero,
 * Heebo headings, tinted icon chips using colors.primary.accessible) so the
 * panel and the statement page read as one product.
 *
 * Behavior is wired 1:1 to SimpleAccessibilityContext's existing public API
 * (fontSize / highContrast / highlightLinks / disableAnimations /
 * increasedSpacing + their setters + resetAll + announceChange) - nothing in
 * that context was changed.
 */

import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogContent,
  GlobalStyles,
  Slide,
  IconButton,
  ToggleButton,
  ToggleButtonGroup,
  Stack,
} from '@mui/material';
import {
  Accessibility,
  X as CloseIcon,
  ALargeSmall,
  Contrast,
  Link2,
  ZapOff,
  AlignJustify,
  RotateCcw,
} from 'lucide-react';
import { useAccessibility, ScreenReaderOnly } from '../contexts/SimpleAccessibilityContext';
import { colors } from '../theme/tokens';
import BrandSwitch from './BrandSwitch';

// Unique id on the Dialog Paper. Used only to scope the focus-ring override
// below (see GlobalStyles comment) - never touched by the a11y context.
const PANEL_ID = 'a11y-settings-panel';

const FONT_HEAD = '"Heebo", sans-serif';
// "Assistant" was never loaded (see components/legal/LegalDocument.js) - it always
// resolved to Heebo. Dropping the name changes nothing that renders.
const FONT_BODY = '"Heebo", sans-serif';
const ICON_COLOR = colors.primary.accessible;

const FONT_SIZE_LABELS = { small: 'קטן', normal: 'רגיל', large: 'גדול' };

// Slide-up transition, matching the widget's Zoom (both live in the same
// file family and both respect "disable animations" via transitionDuration).
const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

/**
 * The brand switch now lives in `components/BrandSwitch.js`.
 *
 * This file used to carry its own copy, hardcoded to RTL (`right` anchor, a
 * negated `translateX`) because this dialog is always Hebrew. The shared one
 * reads the direction off the nearest `dir` ancestor instead, and reproduces
 * this dialog's geometry exactly at `size="medium"` - 46x26 with a 20px thumb,
 * `colors.primary.accessible` when checked - so nothing here renders
 * differently. See that file's docstring for why MUI's own offsets are wrong
 * under `dir="rtl"`.
 */

const RowIcon = ({ Icon }) => (
  <Box
    aria-hidden="true"
    sx={{
      width: 40,
      height: 40,
      borderRadius: '12px',
      flexShrink: 0,
      background: colors.primary.alpha08,
      border: `1px solid ${colors.primary.alpha18}`,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    }}
  >
    <Icon size={19} color={ICON_COLOR} strokeWidth={1.8} />
  </Box>
);

const SettingRow = ({ icon, title, description, isLast, children }) => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: 1.75,
      py: 2,
      borderBottom: isLast ? 'none' : `1px solid ${colors.primary.alpha08}`,
    }}
  >
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.6, minWidth: 0 }}>
      <RowIcon Icon={icon} />
      <Box sx={{ minWidth: 0 }}>
        <Typography
          component="p"
          sx={{ fontFamily: FONT_HEAD, fontWeight: 700, fontSize: '15px', color: '#1A1A2E', mb: 0.25 }}
        >
          {title}
        </Typography>
        <Typography
          component="p"
          sx={{ fontFamily: FONT_BODY, fontSize: '13px', color: '#5A6A7E', lineHeight: 1.5 }}
        >
          {description}
        </Typography>
      </Box>
    </Box>
    <Box sx={{ flexShrink: 0 }}>{children}</Box>
  </Box>
);

const SimpleAccessibilitySettings = ({ open, onClose }) => {
  const firstFocusRef = useRef(null);
  const [resetConfirm, setResetConfirm] = useState(false);

  const {
    // Current settings
    fontSize,
    highContrast,
    highlightLinks,
    disableAnimations,
    increasedSpacing,

    // Actions
    setFontSize,
    toggleHighContrast,
    toggleHighlightLinks,
    toggleAnimations,
    toggleTextSpacing,
    resetAll,
    announceChange,
  } = useAccessibility();

  // Focus management when dialog opens (unchanged behavior: close button
  // receives initial focus).
  useEffect(() => {
    if (open && firstFocusRef.current) {
      setTimeout(() => {
        firstFocusRef.current?.focus();
      }, 100);
    }
    if (!open) {
      setResetConfirm(false);
    }
  }, [open]);

  const handleFontSizeChange = (event, newSize) => {
    if (newSize !== null) {
      setFontSize(newSize);
      announceChange(`גודל הטקסט שונה ל${FONT_SIZE_LABELS[newSize]}`);
    }
  };

  const handleReset = () => {
    resetAll();
    setResetConfirm(false);
    announceChange('כל הגדרות הנגישות אופסו לברירת המחדל');
  };

  return (
    <>
      {/*
        Focus-ring override, scoped by ID (not a class) so it out-specifies
        simple-accessibility.css's global rule:
          *:focus-visible:not(...) { outline: 2px solid #0066CC !important; }
        That rule's six chained :not() clauses give it higher specificity
        than any class-based override even with !important on both sides -
        only an ID selector reliably wins. This keeps the sitewide fallback
        untouched everywhere else while making this panel's rings the
        required brand-accessible teal (colors.primary.accessible).
      */}
      <GlobalStyles
        styles={{
          [`#${PANEL_ID} button:focus-visible, #${PANEL_ID} [role="button"]:focus-visible`]: {
            outline: `2px solid ${colors.primary.accessible} !important`,
            outlineOffset: '2px !important',
          },
        }}
      />
      <Dialog
        open={open}
        onClose={onClose}
        TransitionComponent={Transition}
        transitionDuration={disableAnimations ? 0 : undefined}
        aria-labelledby="accessibility-dialog-title"
        aria-describedby="accessibility-dialog-description"
        keepMounted={false}
        sx={{
          '& .MuiDialog-container': {
            alignItems: { xs: 'flex-end', sm: 'center' },
          },
        }}
        PaperProps={{
          dir: 'rtl',
          id: PANEL_ID,
          sx: {
            width: '100%',
            maxWidth: { xs: '100%', sm: 472 },
            maxHeight: { xs: '90vh', sm: '88vh' },
            m: { xs: 0, sm: 2 },
            borderRadius: { xs: '26px 26px 0 0', sm: '22px' },
            background: 'rgba(255,255,255,0.98)',
            border: `1px solid ${colors.primary.alpha18}`,
            boxShadow: '0 24px 70px -12px rgba(20,30,45,0.35), 0 4px 16px rgba(20,30,45,0.10)',
            overflow: 'hidden',
          },
        }}
      >
        {/* Bottom-sheet grabber - decorative affordance, mobile only */}
        <Box
          aria-hidden="true"
          sx={{
            display: { xs: 'flex', sm: 'none' },
            justifyContent: 'center',
            pt: 1.25,
            pb: 0.25,
            flexShrink: 0,
          }}
        >
          <Box sx={{ width: 40, height: 4, borderRadius: 999, background: colors.neutral[300] }} />
        </Box>

        {/* Hero header band */}
        <Box
          sx={{
            flexShrink: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1.5,
            px: { xs: 2.5, sm: 2.75 },
            py: { xs: 2, sm: 2.5 },
            background: 'linear-gradient(150deg, #EDFAF8 0%, #F8FDFF 45%, #FDF3F7 100%)',
            borderBottom: `1px solid ${colors.primary.alpha18}`,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
            <Box
              aria-hidden="true"
              sx={{
                width: 44,
                height: 44,
                borderRadius: '14px',
                flexShrink: 0,
                background: colors.primary.alpha10,
                border: `1px solid ${colors.primary.alpha20}`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Accessibility size={22} color={ICON_COLOR} strokeWidth={1.8} />
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography
                id="accessibility-dialog-title"
                component="h2"
                sx={{ fontFamily: FONT_HEAD, fontWeight: 800, fontSize: '19px', color: '#1A1A2E', lineHeight: 1.25 }}
              >
                הגדרות נגישות
              </Typography>
              <Typography
                id="accessibility-dialog-description"
                sx={{ fontFamily: FONT_BODY, fontWeight: 500, fontSize: '12.5px', color: ICON_COLOR, opacity: 0.85, mt: 0.25 }}
              >
                התאימו את התצוגה לצרכים שלכם
              </Typography>
            </Box>
          </Box>
          <IconButton
            ref={firstFocusRef}
            onClick={onClose}
            aria-label="סגירת הגדרות נגישות"
            sx={{
              width: 38,
              height: 38,
              borderRadius: '12px',
              flexShrink: 0,
              color: '#4A5568',
              background: 'rgba(255,255,255,0.6)',
              '&:hover': { background: '#fff', color: '#1A1A2E' },
            }}
          >
            <CloseIcon size={18} strokeWidth={2} aria-hidden="true" />
          </IconButton>
        </Box>

        <DialogContent sx={{ p: 0 }}>
          <Box sx={{ px: { xs: 2.5, sm: 2.75 }, pt: 0.5, pb: { xs: 3, sm: 2.75 } }}>
            <SettingRow icon={ALargeSmall} title="התאמת גודל הטקסט" description="הגדלה או הקטנה של הגופן לקריאה נוחה יותר">
              <ToggleButtonGroup
                value={fontSize}
                exclusive
                onChange={handleFontSizeChange}
                aria-label="גודל טקסט"
                sx={{
                  p: '3px',
                  gap: '2px',
                  borderRadius: '12px',
                  background: colors.primary.alpha04,
                  border: `1px solid ${colors.primary.alpha12}`,
                  '& .MuiToggleButtonGroup-grouped': {
                    border: 'none',
                    margin: 0,
                    borderRadius: '9px !important',
                    minHeight: 34,
                    minWidth: 44,
                    px: 1.6,
                    fontFamily: FONT_BODY,
                    fontWeight: 700,
                    fontSize: '13px',
                    color: colors.neutral[600],
                    textTransform: 'none',
                    '&.Mui-selected': {
                      background: '#fff',
                      color: colors.primary.accessible,
                      boxShadow: '0 1px 4px rgba(0,90,82,0.18)',
                    },
                    '&.Mui-selected:hover': { background: '#fff' },
                    '&:hover': { color: '#1A1A2E', background: 'transparent' },
                  },
                }}
              >
                <ToggleButton value="small" aria-label="גופן קטן">קטן</ToggleButton>
                <ToggleButton value="normal" aria-label="גופן רגיל">רגיל</ToggleButton>
                <ToggleButton value="large" aria-label="גופן גדול">גדול</ToggleButton>
              </ToggleButtonGroup>
            </SettingRow>

            <SettingRow icon={Contrast} title="מצב ניגודיות גבוהה" description="הגברת הניגודיות בין הטקסט לרקע לשיפור הראות">
              <BrandSwitch
                checked={highContrast}
                onChange={() => {
                  toggleHighContrast();
                  announceChange(`מצב ניגודיות גבוהה ${!highContrast ? 'הופעל' : 'כובה'}`);
                }}
                inputProps={{ 'aria-label': highContrast ? 'ביטול מצב ניגודיות גבוהה' : 'הפעלת מצב ניגודיות גבוהה' }}
              />
            </SettingRow>

            <SettingRow icon={Link2} title="הדגשת קישורים" description="קו תחתון והדגשה של כל הקישורים באתר">
              <BrandSwitch
                checked={highlightLinks}
                onChange={() => {
                  toggleHighlightLinks();
                  announceChange(`הדגשת קישורים ${!highlightLinks ? 'הופעלה' : 'כובתה'}`);
                }}
                inputProps={{ 'aria-label': highlightLinks ? 'ביטול הדגשת קישורים' : 'הפעלת הדגשת קישורים' }}
              />
            </SettingRow>

            <SettingRow icon={ZapOff} title="עצירת אנימציות" description="ביטול תנועות ומעברים באתר">
              <BrandSwitch
                checked={disableAnimations}
                onChange={() => {
                  toggleAnimations();
                  announceChange(`אנימציות ${disableAnimations ? 'הופעלו' : 'הושבתו'}`);
                }}
                inputProps={{ 'aria-label': disableAnimations ? 'הפעלת אנימציות' : 'עצירת אנימציות' }}
              />
            </SettingRow>

            <SettingRow
              icon={AlignJustify}
              title="הגדלת ריווח הטקסט"
              description="הוספת מרווח בין שורות ואותיות לשיפור הקריאוּת"
              isLast
            >
              <BrandSwitch
                checked={increasedSpacing}
                onChange={() => {
                  toggleTextSpacing();
                  announceChange(`ריווח הטקסט ${!increasedSpacing ? 'הוגדל' : 'הוחזר לרגיל'}`);
                }}
                inputProps={{ 'aria-label': increasedSpacing ? 'ביטול הגדלת ריווח הטקסט' : 'הפעלת הגדלת ריווח הטקסט' }}
              />
            </SettingRow>

            {/* Keyboard shortcut */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.75,
                mt: 2.25,
                p: { xs: 1.75, sm: 2 },
                borderRadius: '16px',
                background: colors.primary.alpha04,
                border: `1px solid ${colors.primary.alpha12}`,
              }}
            >
              <Box
                component="kbd"
                dir="ltr"
                sx={{
                  flexShrink: 0,
                  fontFamily: '"Consolas", monospace',
                  fontWeight: 700,
                  fontSize: '13px',
                  color: '#1A1A2E',
                  background: '#fff',
                  border: `1px solid ${colors.primary.alpha20}`,
                  borderRadius: '9px',
                  px: 1.5,
                  py: 0.75,
                }}
              >
                Alt + A
              </Box>
              <Typography sx={{ fontFamily: FONT_BODY, fontSize: '13.5px', color: '#3A4A5E', lineHeight: 1.5 }}>
                קיצור מקלדת <strong style={{ color: '#1A1A2E' }}>לפתיחה מהירה</strong> של הגדרות אלו מכל עמוד באתר
              </Typography>
            </Box>

            {/* Reset */}
            <Box sx={{ mt: 2.5 }}>
              {resetConfirm ? (
                <Box
                  sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 1.25,
                    p: 1.75,
                    borderRadius: '14px',
                    background: colors.error.alpha10,
                    border: '1px solid rgba(211,47,47,0.22)',
                  }}
                >
                  <Typography sx={{ fontFamily: FONT_BODY, fontSize: '13.5px', fontWeight: 600, color: '#7A1F1F' }}>
                    לאפס את כל הגדרות הנגישות לברירת המחדל?
                  </Typography>
                  <Stack direction="row" spacing={1}>
                    <Button size="small" onClick={handleReset} variant="contained" color="error" sx={{ borderRadius: '10px', minHeight: 36 }}>
                      איפוס
                    </Button>
                    <Button size="small" onClick={() => setResetConfirm(false)} variant="outlined" color="inherit" sx={{ borderRadius: '10px', minHeight: 36 }}>
                      ביטול
                    </Button>
                  </Stack>
                </Box>
              ) : (
                <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                  <Button
                    onClick={() => setResetConfirm(true)}
                    variant="outlined"
                    color="inherit"
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 1,
                      borderRadius: 999,
                      minHeight: 44,
                      px: 2.5,
                      fontFamily: FONT_BODY,
                      fontWeight: 700,
                      fontSize: '13.5px',
                      color: '#3A4A5E',
                      borderColor: colors.neutral[300],
                      textTransform: 'none',
                      '&:hover': { background: colors.neutral[100], borderColor: colors.neutral[400], color: '#1A1A2E' },
                    }}
                  >
                    <RotateCcw size={16} strokeWidth={2} aria-hidden="true" />
                    איפוס כל ההגדרות
                  </Button>
                </Box>
              )}
            </Box>
          </Box>
        </DialogContent>

        {/* Screen Reader Announcements */}
        <ScreenReaderOnly>
          <div role="status" aria-live="polite" aria-atomic="true">
            לוח הגדרות הנגישות פתוח. השתמשו במקש Tab לניווט בין האפשרויות.
          </div>
        </ScreenReaderOnly>
      </Dialog>
    </>
  );
};

export default SimpleAccessibilitySettings;
